import type {
  CheckoutAttribution,
  CheckoutInterval,
  CheckoutPlan,
} from '@klab-berlin/api-sdk/lib/types/requests/Billing';
import { getAttributionEventProperties } from '../../../utils/attributionAmplitude';
import type { CheckoutErrorKind, ErrorData, StripeCheckout } from './checkout.types';
import { TERM_MONTHS, VALID_INTERVALS, VALID_PLANS } from './checkout.constants';

export function isValidPlan(value: unknown): value is CheckoutPlan {
  return typeof value === 'string' && (VALID_PLANS as readonly string[]).includes(value);
}

export function isValidInterval(value: unknown): value is CheckoutInterval {
  return typeof value === 'string' && (VALID_INTERVALS as readonly string[]).includes(value);
}

// SM-309: marketing attribution for the checkout-session payloads — the flat
// 12-key object (initial_* first touch from the cookie, utm_* last touch from
// URL or cookie). Priority rules live in attributionAmplitude, not here.
// Returns undefined when nothing resolves (no cookie / declined consent /
// clean URL) or on any failure, so callers omit the `attribution` key entirely
// and checkout can never break on attribution.
export function resolveCheckoutAttribution(): CheckoutAttribution | undefined {
  try {
    const properties = getAttributionEventProperties();
    return Object.keys(properties).length > 0 ? properties : undefined;
  } catch {
    return undefined;
  }
}

export function readQueryParam(
  query: Record<string, string | undefined>,
  key: string,
): string | undefined {
  const raw = query[key];
  return typeof raw === 'string' && raw.length > 0 ? raw : undefined;
}

type Recurring = NonNullable<StripeCheckout['recurring']>;
// `total` and `recurring.dueNext` are distinct Stripe types but share the
// amount fields we read (`total` / `subtotal` / `discount`).
type BillingAmount = StripeCheckout['total'] | Recurring['dueNext'];
type CheckoutTrial = Recurring['trial'];

export interface DerivedBilling {
  trial: CheckoutTrial;
  isYearly: boolean;
  // Amount source for prices: during a trial `total.total` is 0, so the
  // recurring charge lives on `recurring.dueNext`; otherwise it's `total`.
  billing: BillingAmount;
}

export function deriveBilling(checkout: StripeCheckout): DerivedBilling {
  const { total, recurring } = checkout;
  const trial = recurring?.trial ?? null;
  const isYearly = recurring?.interval === 'year';
  const billing = trial && recurring ? recurring.dueNext : total;
  return { trial, isYearly, billing };
}

// Signature of everything the checkout UI renders from: session id,
// confirmability, and the price/discount figures. Applying or removing a promo —
// or swapping the billing interval (SM-375) — updates these amounts *in place*
// on the live session (same `id`, same `canConfirm`), so keying change-detection
// on `id` alone would drop the new prices. During a trial `total.total` is 0 and
// the charge lives on `recurring.dueNext`, so both are folded in. The recurring
// `interval` is folded in too so a monthly↔yearly swap always trips the
// signature even in the (rare) case the term totals happen to match.
export function checkoutSignature(checkout: StripeCheckout): string {
  const { id, canConfirm, total, recurring } = checkout;
  const amount = (b: BillingAmount) =>
    `${b.total.minorUnitsAmount}/${b.subtotal.minorUnitsAmount}/${b.discount.minorUnitsAmount}`;
  return [
    id,
    canConfirm,
    amount(total),
    recurring ? amount(recurring.dueNext) : '',
    recurring?.interval ?? '',
    recurring?.trial?.trialPeriodDays ?? '',
  ].join('|');
}

// Full contract-term total. A yearly plan bills the whole term at once, so its
// recurring amount already covers 12 months; a monthly plan bills one month, so
// multiply by the term length.
export function toTermTotal(minorUnits: number, isYearly: boolean): number {
  return isYearly ? minorUnits : minorUnits * TERM_MONTHS;
}

// Comparable per-month figure. Stripe exposes the recurring charge per interval,
// so a yearly total is divided across the term; a monthly amount is already
// per-month.
export function toPerMonth(minorUnits: number, isYearly: boolean): number {
  return isYearly ? minorUnits / TERM_MONTHS : minorUnits;
}

// HTTP status off an API-SDK error, when present. Kept here so all checkout
// error-shape access lives in one place alongside `mapErrorToKind`.
export function readErrorStatus(err: unknown): number | undefined {
  return (err as { status?: number })?.status;
}

export function mapErrorToKind(err: unknown): CheckoutErrorKind {
  const data = (err as { data?: ErrorData })?.data;
  const code = data?.code ?? data?.error;
  if (
    code === 'InvalidPlan' ||
    code === 'InvalidInterval' ||
    code === 'InvalidPromoCode' ||
    code === 'CheckoutSessionFailed'
  ) {
    return code;
  }
  return 'NetworkError';
}
